import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import ExecutionTable from '../components/common/ExecutionTable';
import StatisticsCard from '../components/common/StatisticsCard';
import ActivityDistributionChart from '../components/common/ActivityDistributionChart';
import ProcessGraph from '../components/common/ProcessGraph';
import api from '../services/api';
import ProcessVisualizationWidgetWrapper from '../components/common/ProcessVisualizationWidgetWrapper';

const HomePage = () => {
  console.log('[HomePage] Component initializing');
  const [caseAttributionExecutions, setCaseAttributionExecutions] = useState([]);
  const [messageClassifierExecutions, setMessageClassifierExecutions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60); // Default 60 seconds if auto-refresh enabled
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  
  // Pagination state for case attribution
  const [caseAttributionPage, setCaseAttributionPage] = useState(1);
  const [caseAttributionTotalPages, setCaseAttributionTotalPages] = useState(1);
  const [caseAttributionSortOrder, setCaseAttributionSortOrder] = useState("desc");
  
  // Pagination state for message classifier
  const [messageClassifierPage, setMessageClassifierPage] = useState(1);
  const [messageClassifierTotalPages, setMessageClassifierTotalPages] = useState(1);
  const [messageClassifierSortOrder, setMessageClassifierSortOrder] = useState("desc");

  // Define fetchData outside useEffect to make it available to the whole component
  const fetchData = async () => {
    console.log('[HomePage] fetchData() started');
    try {
      setLoading(true);
      console.log('[HomePage] Setting loading state to true');
      
      // Fetch paginated executions for case attribution
      console.log('[HomePage] Fetching case attribution executions');
      const caseAttributionResponse = await api.getExecutions(
        "case_attribution",
        "timestamp_start",
        caseAttributionSortOrder,
        caseAttributionPage,
        10
      );
      console.log('[HomePage] Case attribution executions response:', caseAttributionResponse);
      setCaseAttributionExecutions(caseAttributionResponse.data?.executions || []);
      setCaseAttributionTotalPages(caseAttributionResponse.data?.nr_of_pages || 1);
      console.log('[HomePage] Case attribution executions saved to state:', caseAttributionResponse.data?.executions?.length || 0, 'items');
      
      // Fetch paginated executions for message classifier
      console.log('[HomePage] Fetching message classifier executions');
      const messageClassifierResponse = await api.getExecutions(
        "message_classification",
        "timestamp_start",
        messageClassifierSortOrder,
        messageClassifierPage,
        10
      );
      console.log('[HomePage] Message classifier executions response:', messageClassifierResponse);
      setMessageClassifierExecutions(messageClassifierResponse.data?.executions || []);
      setMessageClassifierTotalPages(messageClassifierResponse.data?.nr_of_pages || 1);
      console.log('[HomePage] Message classifier executions saved to state:', messageClassifierResponse.data?.executions?.length || 0, 'items');
      
      // Fetch basic statistics
      console.log('[HomePage] Fetching basic statistics');
      const statisticsResponse = await api.getBasicStatistics();
      console.log('[HomePage] Statistics response:', statisticsResponse);
      setStatistics(statisticsResponse.data);
      console.log('[HomePage] Statistics saved to state');
      
      setLoading(false);
      console.log('[HomePage] Data fetching completed, setting loading state to false');
      return true; // Return success status for promise chaining
    } catch (err) {
      console.error('[HomePage] Error fetching data:', err);
      console.log('[HomePage] Error details:', JSON.stringify(err, Object.getOwnPropertyNames(err)));
      setError('Failed to load data. Please try again later.');
      console.log('[HomePage] Setting error state');
      setLoading(false);
      console.log('[HomePage] Setting loading state to false after error');
      return false; // Return failure status for promise chaining
    }
  };

  // Download events handler
  const downloadEvents = async (eventType) => {
    console.log(`[HomePage] Downloading ${eventType} events`);
    try {
      const response = await api.getEventsAsCSV(eventType);

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${eventType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`[HomePage] ${eventType} downloaded successfully`);
      setSuccessMessage(`${eventType} downloaded successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('[HomePage] Error downloading events:', err);
      setError('Failed to download events. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  useEffect(() => {
    console.log('[HomePage] Component mounted, setting up data fetching');

    // Initial data fetch
    fetchData();
    console.log('[HomePage] Initial data fetch triggered');
    setLastRefreshTime(new Date());
    
    // We'll control the interval with the autoRefresh state
    let intervalId = null;
    
    // Only set up polling if autoRefresh is true
    if (autoRefresh) {
      console.log(`[HomePage] Setting up polling interval (${refreshInterval}s)`);
      intervalId = setInterval(() => {
        console.log('[HomePage] Polling interval triggered, fetching new data');
        fetchData();
        setLastRefreshTime(new Date());
      }, refreshInterval * 1000);
    }
    
    // Cleanup function
    return () => {
      if (intervalId) {
        console.log('[HomePage] Component unmounting, clearing interval');
        clearInterval(intervalId);
      }
    }
  }, [autoRefresh, refreshInterval, caseAttributionPage, caseAttributionSortOrder, messageClassifierPage, messageClassifierSortOrder]);

  console.log('[HomePage] Render phase. Loading:', loading, 'Error:', error);
  
  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading data...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>NextLog Dashboard</h1>
        
        <div className="d-flex align-items-center">
          <Button
            variant="primary"
            onClick={() => {
              console.log('[HomePage] Manual refresh button clicked');
              setLoading(true);
              fetchData().then(() => {
                setLastRefreshTime(new Date());
                setLoading(false);
              });
            }}
            disabled={loading}
            className="me-2"
          >
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </Button>
          
          <Button
            variant="danger"
            onClick={async () => {
              console.log('[HomePage] Reset button clicked');
              if (window.confirm('Are you sure you want to reset the processing demo? This will clear all data.')) {
                setLoading(true);
                try {
                  await api.resetProcessingDemo();
                  console.log('[HomePage] Reset completed successfully');
                  // Reload the page after successful reset
                  window.location.reload();
                } catch (error) {
                  console.error('[HomePage] Error during reset:', error);
                  setError('Failed to reset processing demo. Please try again.');
                  setLoading(false);
                }
              }
            }}
            disabled={loading}
            className="me-3"
          >
            Reset
          </Button>
          
          <Form.Check 
            type="switch"
            id="auto-refresh-switch"
            label={`Auto-refresh ${autoRefresh ? 'ON' : 'OFF'}`}
            checked={autoRefresh}
            onChange={(e) => {
              console.log(`[HomePage] Auto-refresh switched to ${e.target.checked}`);
              setAutoRefresh(e.target.checked);
            }}
            className="me-2"
          />
          
          {autoRefresh && (
            <Form.Select 
              size="sm" 
              style={{width: '180px'}}
              value={refreshInterval}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                console.log(`[HomePage] Refresh interval changed to ${value}s`);
                setRefreshInterval(value);
              }}
            >
              <option value="30">Refresh every 30s</option>
              <option value="60">Refresh every 1m</option>
              <option value="300">Refresh every 5m</option>
              <option value="600">Refresh every 10m</option>
            </Form.Select>
          )}
        </div>
      </div>
      
      {lastRefreshTime && (
        <div className="text-muted mb-3" style={{fontSize: '0.8rem'}}>
          Last updated: {lastRefreshTime.toLocaleTimeString()}
        </div>
      )}
      
      <Row className="mb-1">
        <Col>
          <StatisticsCard statistics={statistics} />
        </Col>
      </Row>

      {successMessage && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {successMessage}
              <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
            </div>
          </Col>
        </Row>
      )}

      <Row className="mb-4">
        <Col>
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Download Event Datasets</h5>
              <p className="text-muted small mb-3">
                Export event data in CSV format for analysis and reporting
              </p>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-primary"
                  onClick={() => downloadEvents('native_events')}
                  disabled={loading}
                >
                  Download Native Events
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => downloadEvents('source_events')}
                  disabled={loading}
                >
                  Download Source Events
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => downloadEvents('mixed_events')}
                  disabled={loading}
                >
                  Download Mixed Events
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6} className="mb-4 mb-md-0">
          <ActivityDistributionChart 
            activityStatistics={statistics?.activity_statistics || []} 
          />
        </Col>
        <Col md={6} >
          {/* <ProcessGraph /> */}
          <ProcessVisualizationWidgetWrapper 
            dbSource='mixed_events'
            onNodeClick={(nodeId) => { console.log(`[HomePage] Node clicked: ${nodeId}`);}}
            onFilterChange={(filter) => {console.log(`[HomePage] Filter changed: ${JSON.stringify(filter)}`);}}
            showFilters={false}
            showMetrics={false}
            showZoomControls={false}
            maxHeight={'550px'}
            messages={{success: ''}}
          />
        </Col>
      </Row>
      
      <Row>
        <Col lg={6} className="mb-4">
          <ExecutionTable
            executions={caseAttributionExecutions}
            title="Recent Case Attribution Executions"
            currentPage={caseAttributionPage}
            totalPages={caseAttributionTotalPages}
            pageSize={10}
            sortOrder={caseAttributionSortOrder}
            onPageChange={(page) => setCaseAttributionPage(page)}
            onSortChange={(sortOrder) => setCaseAttributionSortOrder(sortOrder)}
            showResults={false}
          />
        </Col>
        <Col lg={6} className="mb-4">
          <ExecutionTable
            executions={messageClassifierExecutions}
            title="Recent Message Classification Executions"
            currentPage={messageClassifierPage}
            totalPages={messageClassifierTotalPages}
            pageSize={10}
            sortOrder={messageClassifierSortOrder}
            onPageChange={(page) => setMessageClassifierPage(page)}
            onSortChange={(sortOrder) => setMessageClassifierSortOrder(sortOrder)}
            showResults={false}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default HomePage;